import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { arrayUnion, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { db, storage } from '@/lib/firebase/config';

/**
 * Photos picked offline wait in IndexedDB and are uploaded on the next connection, then
 * appended to the owning doc's `photoUrls`. Firestore's own offline queue can't hold blobs.
 */
interface QueuedPhoto {
  id: string;
  /** Storage object path. */
  path: string;
  /** Firestore doc path whose `photoUrls` gets the download URL. */
  docPath: string;
  file: Blob;
  contentType: string;
}

const DB_NAME = 'team-offline';
const STORE = 'photos';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const idb = await openDb();
  return new Promise((resolve, reject) => {
    const req = run(idb.transaction(STORE, mode).objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Upload now when possible and return the URL; otherwise queue it and return null.
 * Queued photos land on `docPath` once flushPhotoQueue() runs online.
 */
export async function uploadOrQueuePhoto(
  path: string,
  file: File,
  docPath: string
): Promise<string | null> {
  if (navigator.onLine) {
    try {
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, file, { contentType: file.type });
      return await getDownloadURL(fileRef);
    } catch (err) {
      // Lie-fi / dropped mid-upload: fall through to the queue. Real errors (rules) rethrow.
      if (
        err instanceof FirebaseError &&
        err.code !== 'storage/retry-limit-exceeded' &&
        err.code !== 'storage/unknown'
      ) {
        throw err;
      }
    }
  }
  const entry: QueuedPhoto = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    path,
    docPath,
    file,
    contentType: file.type,
  };
  await tx('readwrite', (s) => s.put(entry));
  return null;
}

let flushing = false;

/** Upload queued photos and attach them to their docs. Safe to call often. */
export async function flushPhotoQueue(): Promise<void> {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const entries = await tx<QueuedPhoto[]>('readonly', (s) => s.getAll());
    for (const e of entries) {
      try {
        const fileRef = storageRef(storage, e.path);
        await uploadBytes(fileRef, e.file, { contentType: e.contentType });
        const url = await getDownloadURL(fileRef);
        await updateDoc(doc(db, e.docPath), {
          photoUrls: arrayUnion(url),
          updatedAt: serverTimestamp(),
        });
        await tx('readwrite', (s) => s.delete(e.id));
      } catch (err) {
        const code = err instanceof FirebaseError ? err.code : '';
        // The doc is gone or no longer editable (e.g. request closed) — nothing to attach to.
        if (code.endsWith('permission-denied') || code.endsWith('not-found')) {
          console.error('[sync] dropping queued photo', e.path, err);
          await tx('readwrite', (s) => s.delete(e.id));
          continue;
        }
        // Network again — stop and retry on the next flush.
        break;
      }
    }
  } finally {
    flushing = false;
  }
}
